import { getUrlDisplayTitle } from '../util/displayText';
import '../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled from 'styled-components';
import Markdown from 'markdown-to-jsx';
import { IoIosClose } from 'react-icons/io';
import { FiCopy, FiDownload, FiMoreVertical, FiVolume2 } from 'react-icons/fi';
import Modal from './Modals/Modal';
import EmailReaderContent, { EmailReaderTabs } from './EmailReaderContent';
import { FILE_BLUE } from '../appearance/Colors';
import { createMarkdownMediaOptions, InlineVideoLink, isDirectMarkdownVideo } from '../util/inlineMarkdownVideo';
import { normalizeArticleMarkdownTables } from '../util/articleMarkdownTables';
import { ArticleReaderParagraph } from './ArticleTweetEmbed';

const modalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: 760,
        width: 'min(92vw, 760px)',
        maxHeight: '86vh',
        padding: 0,
        border: 'none',
        borderRadius: 10,
        background: '#171b1f',
        color: '#f7f7f7',
        overflow: 'hidden'
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        // The message Workspace is rendered at 2300, so the reader must be above it.
        zIndex: 2400
    }
};

const normalizeComparableText = value => String(value || '')
    .replace(/^#+\s*/, '')
    .replace(/[*_`[\]()]/g, '')
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const normalizeReaderLine = line => line
    .replace(/^(Title|URL Source|Markdown Content):\s*/i, '')
    .trimEnd();

const MARKDOWN_IMAGE_URL_REGEX = /(!\[[^\]]*]\()([^)]+)(\))/g;

const normalizeSubstackArticleImageUrl = rawUrl => {
    const value = String(rawUrl || '').trim();
    if (!value) {
        return value;
    }

    try {
        const parsed = new URL(value);
        const host = parsed.hostname.toLowerCase();
        const pathname = parsed.pathname.toLowerCase();
        const isLegacyNewsletterPath = host.endsWith('.substack.com') && pathname.startsWith('/p/');
        const isSubstackHomePostPath = host === 'substack.com' && pathname.startsWith('/home/post/');
        if (!isLegacyNewsletterPath && !isSubstackHomePostPath) {
            return value;
        }

        const encodedSourceMatch = value.match(/https?%3A%2F%2F[^)\s]+/i);
        if (!encodedSourceMatch?.[0]) {
            return value;
        }

        const decodedSource = decodeURIComponent(encodedSourceMatch[0]);
        return /^https?:\/\//i.test(decodedSource) ? decodedSource : value;
    } catch {
        return value;
    }
};

const normalizeBrokenMarkdownImageUrls = markdown => String(markdown || '').replace(
    MARKDOWN_IMAGE_URL_REGEX,
    (_match, prefix, url, suffix) => `${prefix}${normalizeSubstackArticleImageUrl(url)}${suffix}`
);

const IMAGE_ONLY_MARKDOWN_BLOCK_REGEX = /^!\[[^\]]*]\([^)]+\)$/;
const MARKDOWN_HEADING_BLOCK_REGEX = /^#{1,6}\s+\S/;

const splitMarkdownBlocks = markdown => String(markdown || '')
    .trim()
    .split(/\n{2,}/)
    .map(block => block.trim())
    .filter(Boolean);

const isImageOnlyMarkdownBlock = block => {
    const lines = String(block || '')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

    return lines.length > 0 && lines.every(line => IMAGE_ONLY_MARKDOWN_BLOCK_REGEX.test(line));
};

const isLikelyProseBlock = block => {
    const trimmed = String(block || '').trim();
    if (!trimmed || isImageOnlyMarkdownBlock(trimmed) || MARKDOWN_HEADING_BLOCK_REGEX.test(trimmed)) {
        return false;
    }

    if (/^(?:[-*+]\s|\d+\.\s)/m.test(trimmed)) {
        return false;
    }

    const plainText = trimmed.replace(/\[[^\]]+]\([^)]+\)/g, '$1');
    return plainText.length >= 80;
};

const isTableOfContentsHeadingBlock = block =>
    normalizeComparableText(block) === 'table of contents';

const isMarkdownListOnlyBlock = block => {
    const lines = String(block || '')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

    return lines.length > 0 && lines.every(line => /^(?:[-*+]\s|\d+\.\s)/.test(line));
};

const stripLeadingTableOfContents = markdown => {
    const blocks = splitMarkdownBlocks(markdown);
    if (blocks.length < 2 || !isTableOfContentsHeadingBlock(blocks[0])) {
        return String(markdown || '').trim();
    }

    let endIndex = 1;
    while (endIndex < blocks.length && isMarkdownListOnlyBlock(blocks[endIndex])) {
        endIndex += 1;
    }

    if (endIndex === 1) {
        return String(markdown || '').trim();
    }

    return blocks.slice(endIndex).join('\n\n').trim();
};

const cleanHeadingLabel = value => String(value || '')
    .replace(/\[[^\]]+]\(([^)]+)\)/g, '$1')
    .replace(/[*_`~]/g, '')
    .trim();

const createHeadingSlug = value => cleanHeadingLabel(value)
    .toLowerCase()
    .replace(/&amp;/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'section';

const extractReaderNavigationHeadings = markdown => {
    const headingCounts = new Map();

    return String(markdown || '')
        .split('\n')
        .map(line => line.trim())
        .map(line => {
            const match = line.match(/^(#{2,3})\s+(.+)$/);
            if (!match) {
                return null;
            }

            const level = match[1].length;
            const title = cleanHeadingLabel(match[2]);
            if (!title) {
                return null;
            }

            const baseId = createHeadingSlug(title);
            const count = headingCounts.get(baseId) || 0;
            headingCounts.set(baseId, count + 1);

            return {
                id: count === 0 ? baseId : `${baseId}-${count + 1}`,
                title,
                level,
                matchKey: `${level}:${normalizeComparableText(title)}`
            };
        })
        .filter(Boolean);
};

const redistributeTrailingImageAppendix = markdown => {
    const blocks = splitMarkdownBlocks(markdown);
    if (blocks.length < 3) {
        return String(markdown || '').trim();
    }

    const trailingImageBlocks = [];
    for (let index = blocks.length - 1; index >= 0; index -= 1) {
        if (!isImageOnlyMarkdownBlock(blocks[index])) {
            break;
        }
        trailingImageBlocks.unshift(blocks[index]);
    }

    if (trailingImageBlocks.length < 2) {
        return String(markdown || '').trim();
    }

    const allImageBlocks = blocks.filter(isImageOnlyMarkdownBlock);
    if (allImageBlocks.length !== trailingImageBlocks.length) {
        return String(markdown || '').trim();
    }

    const contentBlocks = blocks.slice(0, blocks.length - trailingImageBlocks.length);
    if (contentBlocks.length === 0) {
        return String(markdown || '').trim();
    }

    const proseIndexes = contentBlocks
        .map((block, index) => (isLikelyProseBlock(block) ? index : -1))
        .filter(index => index >= 0);

    if (proseIndexes.length === 0) {
        return String(markdown || '').trim();
    }

    const imageBucketsByBlockIndex = new Map();
    const firstTarget = proseIndexes[0];
    imageBucketsByBlockIndex.set(firstTarget, [trailingImageBlocks[0]]);

    const remainingImages = trailingImageBlocks.slice(1);
    remainingImages.forEach((imageBlock, imageIndex) => {
        const ratio = (imageIndex + 1) / Math.max(1, remainingImages.length);
        const targetPosition = Math.min(
            proseIndexes.length - 1,
            Math.max(1, Math.ceil(ratio * (proseIndexes.length - 1)))
        );
        const targetBlockIndex = proseIndexes[targetPosition];
        const bucket = imageBucketsByBlockIndex.get(targetBlockIndex) || [];
        bucket.push(imageBlock);
        imageBucketsByBlockIndex.set(targetBlockIndex, bucket);
    });

    const redistributed = [];
    contentBlocks.forEach((block, index) => {
        redistributed.push(block);
        const bucket = imageBucketsByBlockIndex.get(index);
        if (bucket?.length) {
            redistributed.push(...bucket);
        }
    });

    return redistributed.join('\n\n').trim();
};

const stripDuplicatedLeadingTitle = (markdown, title) => {
    const normalizedTitle = normalizeComparableText(title);

    if (!normalizedTitle) {
        return markdown;
    }

    const blocks = markdown.split(/\n{2,}/);
    const keptBlocks = [];
    let accumulatedTitleText = '';
    let isStillAtStart = true;

    for (const block of blocks) {
        const normalizedBlock = normalizeComparableText(block);

        if (isStillAtStart && normalizedBlock) {
            const nextAccumulated = `${accumulatedTitleText} ${normalizedBlock}`.trim();
            const isTitleFragment =
                normalizedTitle === normalizedBlock ||
                normalizedTitle.startsWith(nextAccumulated) ||
                nextAccumulated.startsWith(normalizedTitle);

            if (isTitleFragment) {
                accumulatedTitleText = nextAccumulated;
                continue;
            }

            isStillAtStart = false;
        }

        keptBlocks.push(block);
    }

    return keptBlocks.join('\n\n').trim();
};

const cleanArticleMarkdownForReader = (value, title, { preserveImagePlacement = false } = {}) => {
    const normalizedTitle = normalizeComparableText(title);
    const lines = String(value || '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n');

    const cleaned = [];
    let hasSeenContent = false;

    for (const rawLine of lines) {
        const line = normalizeReaderLine(rawLine);
        const trimmed = line.trim();

        if (!hasSeenContent) {
            if (!trimmed) {
                cleaned.push(line);
                continue;
            }

            if (normalizedTitle && normalizeComparableText(trimmed) === normalizedTitle) {
                continue;
            }
        }

        if (trimmed) {
            hasSeenContent = true;
        }

        cleaned.push(line);
    }

    const cleanedMarkdown = cleaned
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    const normalized = normalizeArticleMarkdownTables(normalizeBrokenMarkdownImageUrls(stripDuplicatedLeadingTitle(cleanedMarkdown, title)));
    return preserveImagePlacement ? normalized : redistributeTrailingImageAppendix(normalized);
};

export const buildArticleReaderPresentation = (articleBody, title, options) => {
    const readableContent = stripLeadingTableOfContents(getReadableArticleContent(articleBody, title, options));
    return {
        content: readableContent,
        navigationHeadings: extractReaderNavigationHeadings(readableContent)
    };
};

export const getReadableArticleContent = (articleBody, title, options) => (
    cleanArticleMarkdownForReader(articleBody, title, options)
);

const buildMarkdownFilename = title => {
    const baseName = String(title || 'saved-article')
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 72)
        .toLowerCase();

    return `${baseName || 'saved-article'}.md`;
};

const copyTextToClipboard = async text => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
};

const VIDEO_LINK_LABEL_REGEX = /^video(?:\s+\d+)?(?:\s*:\s*video)?(?:\s+file)?$/i;
const VIDEO_LIKE_URL_REGEX = /(?:\.(?:mp4|m4v|mov|webm|ogv|m3u8))(?:[?#].*)?$|\/(?:video|media)\/[^/?#]+(?:[/?#].*)?$/i;

const isVideoLikeHref = href => typeof href === 'string' && VIDEO_LIKE_URL_REGEX.test(href);

const isTwitterVideoUrl = href => {
    try {
        const parsed = new URL(href);
        return parsed.hostname.toLowerCase() === 'video.twimg.com';
    } catch {
        return false;
    }
};

const getFirstUsableVideoSource = videoSources => (
    (Array.isArray(videoSources) ? videoSources : [])
        .map(source => String(source || '').trim())
        .filter(source => source && !isTwitterVideoUrl(source))
        .find(Boolean) || null
);

const ReaderLink = ({ children, href, videoSources, ...props }) => {
    const {t: lnT} = useTranslation();

    const safeHref = typeof href === 'string' && href ? href : '#';
    const label = React.Children.toArray(children)
        .filter(child => typeof child === 'string')
        .join('')
        .trim();
    const shouldRenderAsVideo = isVideoLikeHref(safeHref) || VIDEO_LINK_LABEL_REGEX.test(label);
    const isTwitterSource = isTwitterVideoUrl(safeHref);
    const preferredVideoSource = isTwitterSource ? getFirstUsableVideoSource(videoSources) : null;
    const videoSrc = preferredVideoSource || safeHref;

    if (shouldRenderAsVideo && videoSrc) {
        return (
            <InlineVideoLink href={videoSrc} label={label || lnT('messages:videoFile')} surface="dark" />
        );
    }

    return (
        <a {...props} href={safeHref} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    );
};

export const createArticleMarkdownOptions = videoSources => ({
    disableParsingRawHTML: true,
    forceBlock: true,
    ...createMarkdownMediaOptions(),
    overrides: {
        p: { component: ArticleReaderParagraph },
        a: { component: props => <ReaderLink {...props} videoSources={videoSources} /> },
        img: { component: ReaderImage },
        table: { component: ReaderTable }
    }
});

const formatCompactDate = timestamp => {
    const numericTimestamp = Number(timestamp);
    if (!Number.isFinite(numericTimestamp) || numericTimestamp <= 0) {
        return '';
    }

    return new Date(numericTimestamp * 1000).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

const formatCompactMetric = value => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) return null;
    if (num >= 1_000_000) {
        const formatted = (num / 1_000_000).toFixed(1);
        return `${formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted}M`;
    }
    if (num >= 1_000) {
        const formatted = (num / 1_000).toFixed(1);
        return `${formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted}K`;
    }
    return String(num);
};

const buildSourceMetaItems = ({ sourcePlatform, sourceMetrics, sourcePlatformMeta }) => {
    const platform = String(sourcePlatform || '').toLowerCase();
    const items = [];

    if (platform === 'reddit') {
        items.push('Reddit');
        const redditMeta = sourcePlatformMeta?.reddit || {};
        if (redditMeta.subreddit) {
            items.push(`r/${redditMeta.subreddit}`);
        }
        if (Number.isFinite(Number(sourceMetrics?.score))) {
            items.push(`${Number(sourceMetrics.score)} points`);
        }
        if (Number.isFinite(Number(sourceMetrics?.commentCount))) {
            items.push(`${Number(sourceMetrics.commentCount)} comments`);
        }
    } else if (platform === 'tiktok') {
        items.push('TikTok');
        if (sourceMetrics?.viewCount) {
            items.push(`${formatCompactMetric(sourceMetrics.viewCount)} views`);
        }
        if (sourceMetrics?.likeCount) {
            items.push(`${formatCompactMetric(sourceMetrics.likeCount)} likes`);
        }
        if (sourceMetrics?.commentCount) {
            items.push(`${formatCompactMetric(sourceMetrics.commentCount)} comments`);
        }
        if (sourceMetrics?.engagementRate) {
            items.push(`${(sourceMetrics.engagementRate * 100).toFixed(1)}% engagement`);
        }
    } else if (platform === 'instagram') {
        items.push('Instagram');
        if (sourceMetrics?.viewCount) {
            items.push(`${formatCompactMetric(sourceMetrics.viewCount)} views`);
        }
        if (sourceMetrics?.likeCount) {
            items.push(`${formatCompactMetric(sourceMetrics.likeCount)} likes`);
        }
        if (sourceMetrics?.commentCount) {
            items.push(`${formatCompactMetric(sourceMetrics.commentCount)} comments`);
        }
        if (sourceMetrics?.engagementRate) {
            items.push(`${(sourceMetrics.engagementRate * 100).toFixed(1)}% engagement`);
        }
    } else if (platform === 'twitter' || platform === 'x') {
        items.push('X');
        if (sourceMetrics?.viewCount) {
            items.push(`${formatCompactMetric(sourceMetrics.viewCount)} views`);
        }
        if (sourceMetrics?.likeCount) {
            items.push(`${formatCompactMetric(sourceMetrics.likeCount)} likes`);
        }
        if (sourceMetrics?.commentCount) {
            items.push(`${formatCompactMetric(sourceMetrics.commentCount)} replies`);
        }
        if (sourceMetrics?.engagementRate) {
            items.push(`${(sourceMetrics.engagementRate * 100).toFixed(1)}% engagement`);
        }
    } else if (sourceMetrics) {
        if (sourceMetrics.viewCount) {
            items.push(`${formatCompactMetric(sourceMetrics.viewCount)} views`);
        }
        if (sourceMetrics.likeCount) {
            items.push(`${formatCompactMetric(sourceMetrics.likeCount)} likes`);
        }
        if (sourceMetrics.commentCount) {
            items.push(`${formatCompactMetric(sourceMetrics.commentCount)} comments`);
        }
        if (sourceMetrics.engagementRate) {
            items.push(`${(sourceMetrics.engagementRate * 100).toFixed(1)}% engagement`);
        }
    }

    const postedDate = formatCompactDate(sourceMetrics?.createdUtc);
    if (postedDate) {
        items.push(postedDate);
    }

    return items;
};

function ArticleReaderModal({
    isOpen,
    onRequestClose,
    title,
    articleBody,
    messageId,
    videoSources,
    redditComments,
    sourcePlatform,
    sourceMetrics,
    sourcePlatformMeta,
    onCreateAudio,
    isCreateAudioDisabled = false,
    isCreatingAudio = false
}) {
    const {t: lnT} = useTranslation();

    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [hasCopied, setHasCopied] = React.useState(false);
    const [emailText, setEmailText] = React.useState('');
    const [emailView, setEmailView] = React.useState('original');
    React.useEffect(() => { setEmailView('original'); }, [messageId]);
    React.useEffect(() => { setEmailText(''); }, [messageId]);
    const menuRef = React.useRef(null);
    const { content } = React.useMemo(
        () => buildArticleReaderPresentation(sourcePlatform === 'email' && messageId ? emailText : articleBody, title, { preserveImagePlacement: sourcePlatform === 'email' }),
        [lnT, articleBody, title, sourcePlatform, messageId, emailText]
    );
    const normalizedRedditComments = React.useMemo(
        () => (Array.isArray(redditComments) ? redditComments : [])
            .map(comment => ({
                author: String(comment?.author || '').trim(),
                body: cleanArticleMarkdownForReader(comment?.body || '', ''),
                score: Number.isFinite(Number(comment?.score)) ? Number(comment.score) : null,
                permalink: String(comment?.permalink || '').trim()
            }))
            .filter(comment => comment.body),
        [lnT, redditComments]
    );
    const sourceMetaItems = React.useMemo(
        () => buildSourceMetaItems({ sourcePlatform, sourceMetrics, sourcePlatformMeta }),
        [lnT, sourcePlatform, sourceMetrics, sourcePlatformMeta]
    );
    const filename = React.useMemo(() => buildMarkdownFilename(title), [lnT, title]);

    const markdownOptions = React.useMemo(() => createArticleMarkdownOptions(videoSources), [lnT, videoSources]);

    React.useEffect(() => {
        if (!isOpen) {
            setIsMenuOpen(false);
            setHasCopied(false);
        }
    }, [isOpen]);

    React.useEffect(() => {
        if (!isMenuOpen) {
            return undefined;
        }

        const handlePointerDown = event => {
            if (menuRef.current?.contains(event.target)) {
                return;
            }

            setIsMenuOpen(false);
        };

        document.addEventListener('pointerdown', handlePointerDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [isMenuOpen]);

    const handleCopyMarkdown = async () => {
        await copyTextToClipboard(content);
        setHasCopied(true);
        setIsMenuOpen(false);
        window.setTimeout(() => setHasCopied(false), 1800);
    };

    const handleDownloadMarkdown = () => {
        const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
        setIsMenuOpen(false);
    };

    const handleCreateAudio = () => {
        if (typeof onCreateAudio !== 'function' || isCreateAudioDisabled) {
            return;
        }

        onCreateAudio(content);
        setIsMenuOpen(false);
    };

    return (
        <Modal
            isOpen={isOpen && (!!content || (sourcePlatform === 'email' && !!messageId))}
            onRequestClose={onRequestClose}
            style={modalStyles}
            contentLabel={lnT('messages:readSavedArticle')}
        >
        <ReaderShell $email={sourcePlatform === 'email'}>
            <ReaderHeader>
                <CloseButton type="button" onClick={onRequestClose} aria-label={lnT('messages:closeReader')}>
                    <IoIosClose size={28} />
                </CloseButton>
                <ReaderTitleBlock>
                    <ReaderTitle title={title || lnT('messages:savedArticle')}>{getUrlDisplayTitle(title) || lnT('messages:savedArticle')}</ReaderTitle>
                    {sourceMetaItems.length > 0 ? (
                        <SourceMetaRow>{sourceMetaItems.join(' - ')}</SourceMetaRow>
                    ) : null}
                </ReaderTitleBlock>
                <ReaderActions ref={menuRef}>
                    <MoreButton
                        type="button"
                        onClick={() => setIsMenuOpen(current => !current)}
                        aria-label={lnT('messages:articleActions')}
                        aria-expanded={isMenuOpen}
                    >
                        <FiMoreVertical size={18} />
                    </MoreButton>
                    {isMenuOpen ? (
                        <ActionsMenu>
                            <MenuButton type="button" onClick={handleCopyMarkdown}>
                                <FiCopy size={14} />
                                <span>{hasCopied ? lnT('messages:copied_8d525e5f') : lnT('messages:copyMd')}</span>
                            </MenuButton>
                            <MenuButton type="button" onClick={handleDownloadMarkdown}>
                                <FiDownload size={14} />
                                <span>{lnT('messages:downloadMd')}</span>
                            </MenuButton>
                            {typeof onCreateAudio === 'function' ? (
                                <MenuButton
                                    type="button"
                                    onClick={handleCreateAudio}
                                    disabled={isCreateAudioDisabled}
                                    title={isCreatingAudio ? lnT('messages:generatingAudio') : lnT('messages:createAudio_c78eddd9')}
                                >
                                    <FiVolume2 size={14} />
                                    <span>{isCreatingAudio ? lnT('messages:startingAudio') : lnT('messages:createAudio')}</span>
                                </MenuButton>
                            ) : null}
                        </ActionsMenu>
                    ) : null}
                </ReaderActions>
            </ReaderHeader>
            <ReaderBody $email={sourcePlatform === 'email'}>
                <EmailReaderContent enabled={sourcePlatform === 'email'} messageId={messageId} onTextLoaded={setEmailText} fullHeight={sourcePlatform === 'email'} view={emailView} showControls={sourcePlatform !== 'email'}>
                {sourcePlatform === 'email' ? text => <Markdown options={markdownOptions}>{text}</Markdown> :
                    <Markdown options={markdownOptions}>{content}</Markdown>}
                </EmailReaderContent>
                {normalizedRedditComments.length > 0 ? (
                    <CommentsSection>
                        <CommentsTitle>{lnT('messages:comments')}</CommentsTitle>
                        {normalizedRedditComments.map((comment, index) => (
                            <CommentBlock key={`${comment.permalink || comment.author || 'comment'}-${index}`}>
                                <CommentMeta>
                                    <span>{comment.author || lnT('messages:redditUser')}</span>
                                    {comment.score !== null ? <span>{comment.score}{lnT('messages:points')}</span> : null}
                                </CommentMeta>
                                <Markdown
                                    options={markdownOptions}
                                >
                                    {comment.body}
                                </Markdown>
                                {comment.permalink ? <CommentLink href={comment.permalink} target="_blank" rel="noopener noreferrer">{lnT('messages:openComment')}</CommentLink> : null}
                            </CommentBlock>
                        ))}
                    </CommentsSection>
                ) : null}
            </ReaderBody>
        </ReaderShell>
        </Modal>
    );
}

const ReaderShell = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 86vh;
    height: ${props => props.$email ? '86vh' : 'auto'};
`;

const ReaderHeader = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ReaderTitleBlock = styled.div`
    flex: 1;
    min-width: 0;
`;

const ReaderTitle = styled.div`
    color: #fff;
    font-size: 14px;
    line-height: 19px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const SourceMetaRow = styled.div`
    margin-top: 2px;
    color: rgba(255, 255, 255, 0.52);
    font-size: 12px;
    line-height: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CloseButton = styled.button`
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.78);
    background: transparent;
    cursor: pointer;

    &:hover,
    &:focus-visible {
        background: rgba(255, 255, 255, 0.08);
    }
`;

const ReaderActions = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
`;

const MoreButton = styled.button`
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.78);
    background: transparent;
    cursor: pointer;

    &:hover,
    &:focus-visible {
        background: rgba(255, 255, 255, 0.08);
    }
`;

const ActionsMenu = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 2;
    min-width: 154px;
    padding: 6px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    background: #22272d;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.32);
`;

const MenuButton = styled.button`
    width: 100%;
    border: 0;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 10px;
    color: rgba(255, 255, 255, 0.9);
    background: transparent;
    font-size: 13px;
    line-height: 16px;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }

    &:disabled:hover {
        background: transparent;
    }
`;

const ReaderBody = styled.div`
    padding: ${props => props.$email ? '0' : '18px 24px 30px'};
    flex: ${props => props.$email ? '1 1 auto' : 'initial'};
    min-height: 0;
    overflow: ${props => props.$email ? 'hidden' : 'auto'};
    color: rgba(255, 255, 255, 0.9);
    font-size: 15px;
    line-height: 1.48;

    > * {
        max-width: ${props => props.$email ? '100%' : '620px'};
        margin-left: auto;
        margin-right: auto;
    }

    h1, h2, h3, h4, h5, h6 {
        color: #fff;
        line-height: 1.35;
        margin-top: 1.2em;
        margin-bottom: 0.55em;
        letter-spacing: 0;
    }

    h1 {
        font-size: 18px;
    }

    h2 {
        font-size: 17px;
    }

    h3 {
        font-size: 16px;
    }

    h4,
    h5,
    h6 {
        font-size: 15px;
    }

    h1:first-child,
    h2:first-child,
    h3:first-child {
        margin-top: 0;
    }

    p,
    ul,
    ol,
    blockquote,
    pre {
        margin: 0;
    }

    p {
        font-size: 15px;
        line-height: 1.48;
        overflow-wrap: anywhere;
    }

    p + p,
    p + ul,
    p + ol,
    ul + p,
    ol + p,
    p + blockquote,
    blockquote + p {
        margin-top: 8px;
    }

    ul,
    ol {
        padding-left: 1.45em;
        margin-top: 8px;
        margin-bottom: 8px;
    }

    ul {
        list-style-type: disc;
        list-style-position: outside;
    }

    ol {
        list-style-type: decimal;
        list-style-position: outside;
    }

    li {
        display: list-item;
        margin: 4px 0;
        padding-left: 0.16em;
    }

    li::marker {
        color: rgba(255, 255, 255, 0.72);
        font-weight: 600;
    }

    li > p:first-child {
        display: inline;
        margin: 0;
    }

    a {
        color: ${FILE_BLUE};
        overflow-wrap: anywhere;
    }

    blockquote {
        border-left: 3px solid rgba(220, 226, 235, 0.78);
        padding: 4px 0 4px 22px;
        color: rgba(255, 255, 255, 0.94);
        font-style: normal;
        background: transparent;
    }

    blockquote p {
        font-weight: 600;
    }

    code {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
        padding: 1px 4px;
    }

    pre {
        overflow-x: auto;
        background: rgba(0, 0, 0, 0.28);
        border-radius: 8px;
        padding: 12px;
    }

    hr {
        max-width: ${props => props.$email ? '100%' : '620px'};
        border: 0;
        border-top: 1px solid rgba(255, 255, 255, 0.12);
        margin: 18px auto;
    }
`;

const CommentsSection = styled.section`
    max-width: 620px;
    margin: 28px auto 0;
    padding-top: 18px;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const CommentsTitle = styled.h2`
    margin: 0 0 14px;
    color: #fff;
    font-size: 16px;
    line-height: 22px;
    font-weight: 700;
`;

const CommentBlock = styled.article`
    padding: 12px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.08);

    &:first-of-type {
        border-top: 0;
        padding-top: 0;
    }
`;

const CommentMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 6px;
    color: rgba(255, 255, 255, 0.55);
    font-size: 12px;
    line-height: 16px;
`;

const CommentLink = styled.a`
    display: inline-flex;
    margin-top: 6px;
    color: ${FILE_BLUE};
    font-size: 12px;
    line-height: 16px;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const ReaderImageBase = ({ src, alt, fallbackHref, ...props }) => isDirectMarkdownVideo(src) ? (
    <InlineVideoLink href={src} fallbackHref={fallbackHref} label={alt} surface="dark" />
) : (
    <img
        {...props}
        src={normalizeSubstackArticleImageUrl(src)}
        alt={alt}
        referrerPolicy="no-referrer"
    />
);

const ReaderImage = styled(ReaderImageBase)`
    display: block;
    max-width: min(100%, 560px);
    max-height: 320px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
    margin: 10px auto 14px;
`;

const hasTableHeaderContent = children => React.Children.toArray(children).some(child => (
    React.isValidElement(child)
        ? hasTableHeaderContent(child.props.children)
        : String(child || '').trim().length > 0
));

const ReaderTable = ({ children, ...props }) => (
    <ReaderTableScroll>
        <ReaderTableElement {...props}>
            {React.Children.toArray(children).filter(child => !(
                React.isValidElement(child) && child.type === 'thead' && !hasTableHeaderContent(child.props.children)
            ))}
        </ReaderTableElement>
    </ReaderTableScroll>
);

const ReaderTableScroll = styled.div`
    max-width: 100%;
    overflow-x: auto;
    margin: 18px auto;
`;

const ReaderTableElement = styled.table`
    width: 100%;
    table-layout: auto;
    border-collapse: collapse;

    th,
    td {
        border-bottom: 1px solid rgba(255, 255, 255, 0.12);
        padding: 10px 0 10px 16px;
        vertical-align: top;
        text-align: left;
        font-size: 14px;
        line-height: 1.45;
    }

    th:first-child,
    td:first-child {
        width: 1%;
        padding-left: 0;
    }

    th {
        color: #fff;
        border-bottom: 2px solid rgba(255, 255, 255, 0.24);
        font-weight: 700;
    }
`;

export default ArticleReaderModal;
